import React, { useEffect, useState } from "react";
import DateHelpers from '../Helper/DateHelpers.js';
import '../Styles/GroupReservationCard.scss';

// One horizontal card per group reservation on the group dashboard. This replaces the old ag-grid
// row, which pushed the group code, fast track perks, and camper list off to the right where a user
// only found them by scrolling. Everything here comes from data the dashboard already queries.
function GroupReservationCard({ reservation }) {

   const [campersJoined, setCampersJoined] = useState([])
   const [campersVisible, setCampersVisible] = useState(false)
   const [selectedFastTrack, setSelectedFastTrack] = useState()

   const campName = reservation.camp_name || reservation.week_name
   const amountDue = Number(reservation.amount_due)

   // The collapsed card shows the camper count, so the list is pulled on mount rather than on the
   // first expand. The same response then backs the expanded list without a second round trip.
   const getCampersJoined = async () => {
      try {
         const groupCode = reservation.group_code
         const individualsJoinedRequest = await fetch('/individualsjoined', {
            method: 'POST',
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ groupCode })
         })
         const individualsJoinedResponse = await individualsJoinedRequest.json()
         const joined = individualsJoinedResponse.data.selectedRegistrations
         setCampersJoined(Array.isArray(joined) ? joined : [])
      } catch (error) {
         console.error(error.message)
      }
   }

   const getSelectedFastTrack = async () => {
      try {
         const reservationId = reservation.reservation_id
         const fastTrackRequest = await fetch('/getselectedfasttrack', {
            method: 'POST',
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ reservationId })
         })
         const fastTrackResponse = await fastTrackRequest.json()
         setSelectedFastTrack(fastTrackResponse.data.eventInfo[0])
      } catch (error) {
         console.error(error.message)
      }
   }

   useEffect(() => {
      getCampersJoined()
      if (reservation.is_fast_track === true) {
         getSelectedFastTrack()
      }
   }, [])

   // A reservation carries a second counselor promo only when the group took the counselor option
   // instead of the free deluxe room, so the codes are filtered rather than always shown as a pair.
   const counselorPromos = [
      selectedFastTrack?.church_counselor_promo_1,
      selectedFastTrack?.church_counselor_promo_2
   ].filter(Boolean)

   const shareSubject = encodeURIComponent(`Join us for ${campName} at the Bill Rice Ranch.`)
   const shareBody = encodeURIComponent(
      `Use the following code to join our group when you register for ${campName}: ${reservation.group_code}`)

   return (
      <div className="groupReservationCardContainer">
         <div className="groupReservationCardContent">

            <div className="groupReservationCardTopRow">
               <div className="groupReservationCardTitleBlock">
                  <div className="groupReservationCardCampName">
                     {campName}
                  </div>
                  <div className="groupReservationCardDates">
                     {DateHelpers.getEventDateRange(reservation.begin_date, reservation.end_date)}
                  </div>
               </div>
               <div className="groupReservationCardStat groupReservationCardSpotsStat">
                  <div className="groupReservationCardStatLabel">
                     Campers Planned
                  </div>
                  <div className="groupReservationCardSpotCount">
                     {reservation.num_spots_reserved}
                  </div>
               </div>
               <div className="groupReservationCardRightSide">
                  <div className="groupReservationCardStat">
                     <div className="groupReservationCardStatLabel">
                        Group Code
                     </div>
                     <div className="groupReservationCardCodeRow">
                        <div className="groupReservationCardCode">
                           {reservation.group_code}
                        </div>
                        <a
                           className="groupReservationCardShareIcon"
                           href={`mailto:?subject=${shareSubject}&body=${shareBody}`}
                           title="Share this group code with your campers"
                           aria-label="Share this group code with your campers">
                           {/* iOS-style share glyph: an arrow lifting up out of an open box. */}
                           <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                              strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                              <path d="M8 11H6.5A1.5 1.5 0 0 0 5 12.5v7A1.5 1.5 0 0 0 6.5 21h11a1.5 1.5 0 0 0 1.5-1.5v-7a1.5 1.5 0 0 0-1.5-1.5H16" />
                              <path d="M12 3v12" />
                              <path d="M8.5 6.5 12 3l3.5 3.5" />
                           </svg>
                        </a>
                     </div>
                  </div>
                  {amountDue > 0 &&
                     <div className="groupReservationCardAmountDue">
                        Amount Due: ${amountDue.toFixed(2)}
                     </div>
                  }
               </div>
            </div>

            {reservation.is_fast_track === true &&
               <div className="groupReservationCardFastTrack">
                  <div className="groupReservationCardFastTrackBadge">
                     Fast Track
                  </div>
                  <ul className="groupReservationCardFastTrackList">
                     {counselorPromos.map((promoCode) => (
                        <li key={promoCode}>
                           Free week of camp for a church counselor — code
                           <span className="groupReservationCardPromoCode">{promoCode}</span>
                        </li>
                     ))}
                     {selectedFastTrack?.free_room_option === true &&
                        <li>Free deluxe room</li>
                     }
                  </ul>
               </div>
            }

            <div className="groupReservationCardCampersRow">
               <div className="groupReservationCardCampersCount">
                  {campersJoined.length} {campersJoined.length === 1 ? "camper has" : "campers have"} joined
               </div>
               <div className="groupReservationCardCampersButton">
                  <button onClick={() => setCampersVisible(!campersVisible)}>
                     {campersVisible ? "Hide Campers" : "View Campers"}
                  </button>
               </div>
            </div>

            {campersVisible === true &&
               <div className="groupReservationCardCampersPanel">
                  {campersJoined.length > 0 ? (
                     <div className="groupReservationCardCampersList">
                        {campersJoined.map((camper, camperIndex) => (
                           <div className="groupReservationCardCamperRow" key={camperIndex}>
                              <div className="groupReservationCardCamperName">
                                 {camper.first_name + " " + camper.last_name}
                              </div>
                              <div className="groupReservationCardCamperRole">
                                 {camper.role}
                              </div>
                              <div className="groupReservationCardCamperAmountDue">
                                 Amount Due: ${Number(camper.amount_due).toFixed(2)}
                              </div>
                           </div>
                        ))}
                     </div>
                  ) : (
                     <div className="groupReservationCardNoCampers">
                        No campers have joined this group yet.
                     </div>
                  )}
               </div>
            }

         </div>
      </div>
   )
}

export default GroupReservationCard;
